import { formatCurrency, formatDate } from '@angular/common';
import { AfterViewInit, Component, computed, inject, signal, viewChild } from '@angular/core';
import {
  ColDef,
  GetDetailRowDataParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IRowNode,
  RowSelectedEvent,
  ValueFormatterParams,
} from 'ag-grid-community';
import { sortBy, sumBy, uniq } from 'lodash';
import { DateTime } from 'luxon';
import { linkedQueryParam, paramToNumber } from 'ngxtension/linked-query-param';
import { finalize, map } from 'rxjs/operators';
import { defaultTableDefOptions } from '../../../helpers';

import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { PanModalComponent } from '../../../components/pan-modal/pan-modal.component';
import { tryGetErrorMessage } from '../../../error.utils';
import { CaseTrackingService } from '../../../service/case-tracking.service';
import { NotifyService } from '../../../service/notify.service';
import { TheftReportService } from '../../../service/theft-report.service';
import {
  Branch,
  CaseStatus,
  CaseWithIncidents,
  GetCasesByLocationsRequest,
  GroceryCaseIncidentEntry,
  GroceryCaseTab,
  GroceryCaseTableEntry,
  Pagination,
  SharedInvestigation,
  TheftReportAttachmentType,
  TimelineRow,
} from '../../../types';
import { AGGridMenuItem } from '../../components/ag-grid-menu-item/ag-grid-menu-item';
import { GroceryCtpStatusCellComponent } from './grocery-ctp-status-cell/grocery-ctp-status-cell.component';
import { GroceryCtpViewActionCellComponent } from './grocery-ctp-view-action-cell/grocery-ctp-view-action-cell.component';

const INCIDENT_TYPE_LABELS: Record<string, string> = {
  LIC: 'Left in Cart',
  NSCAN: 'Non-Scan',
  PLU: 'Price Look-Up Abuse',
  PLA: 'Price Look-Up Abuse',
  BOB: 'Bottom of Basket',
  INVL: 'Inventory Loss',
  CTXN: 'Canceled Transaction',
  NSL: 'No Sale',
  SRFUND: 'Suspicious Refund',
  LNFP: 'Late Night Food Prep',
};

@Component({
  selector: 'app-pan-grocery-case-portal',
  templateUrl: './grocery-case-portal.component.html',
  styleUrl: './grocery-case-portal.component.scss',
  standalone: false,
})
export class GroceryCasePortalComponent implements AfterViewInit {
  private caseTrackingService = inject(CaseTrackingService);
  private theftReportService = inject(TheftReportService);
  private notifyService = inject(NotifyService);
  private masterGridApi: GridApi<GroceryCaseTableEntry> | undefined;

  public detailModal = viewChild<PanModalComponent>('detailModal');

  public selectedInvestigation = signal<SharedInvestigation | null>(null);
  public isLoadingInvestigation = signal<boolean>(false);
  public selectedIncidentRow = signal<GroceryCaseIncidentEntry | undefined>(undefined);
  public selectedCases = signal<GroceryCaseTableEntry[]>([]);
  public selectedIncidents = signal<GroceryCaseIncidentEntry[]>([]);

  private getMasterRowSelection() {
    return {
      mode: 'multiRow' as const,
      headerCheckbox: this.selectedIncidents().length === 0,
      hideDisabledCheckboxes: true,
      isRowSelectable: (row: IRowNode<GroceryCaseTableEntry>) => {
        if (this.activeTab() === 'archived' && row.data?.status !== CaseStatus.Archived) {
          return false;
        }

        return this.selectedIncidents().length === 0;
      },
    };
  }

  private getDetailRowSelection() {
    return {
      mode: 'multiRow' as const,
      headerCheckbox: this.selectedCases().length === 0,
      hideDisabledCheckboxes: true,
      isRowSelectable: () => this.selectedCases().length === 0,
    };
  }

  private refreshSelectionConstraints() {
    this.masterGridApi?.setGridOption('rowSelection', this.getMasterRowSelection());

    this.masterGridApi?.forEachDetailGridInfo((detailGrid) => {
      detailGrid.api?.setGridOption('rowSelection', this.getDetailRowSelection());
    });
  }

  public timelineRows = computed<TimelineRow[]>(() => {
    const timeline = this.selectedInvestigation()?.timeline;
    if (!timeline) return [];
    return sortBy(
      timeline.map((item) => ({
        when: item.occurred,
        transactionId: item.platform_id,
        timestamp: typeof item.trans_date === 'string' ? item.trans_date : item.trans_date.toISOString(),
        items: item.items.map((i) => `${i.description} (${formatCurrency(Number(i.total), 'en-US', '$')})`).join('\n'),
        highlight: item.occurred === 'walkout' || item.occurred === 'cancellation',
      })),
      (row) => row.timestamp,
    );
  });

  public totalItemCount = computed(() => {
    const timeline = this.selectedInvestigation()?.timeline;
    if (!timeline) return 0;
    return sumBy(timeline, (t) => t.items.length);
  });

  public evidenceItems = computed<{ type: 'video' | 'video-link'; url: string; label: string; password?: string | null }[]>(() => {
    const investigation = this.selectedInvestigation();
    if (!investigation) return [];

    const items: { type: 'video' | 'video-link'; url: string; label: string; password?: string | null }[] = [];

    for (const [i, link] of (investigation.video_links ?? []).entries()) {
      if (link) items.push({ type: 'video', url: link, label: `Video ${i + 1}` });
    }

    for (const att of investigation.attachments ?? []) {
      if (att.type === TheftReportAttachmentType.VideoLink && att.content) {
        items.push({ type: 'video-link', url: att.content, label: 'Video Link', password: att.password });
      }
    }

    return items;
  });

  public currentEvidenceIndex = signal<number>(0);

  public currentEvidenceItem = computed(() => {
    const items = this.evidenceItems();
    return items[this.currentEvidenceIndex()] ?? undefined;
  });

  readonly pageBranchId = linkedQueryParam<number | undefined>('branch_id', {
    parse: (value) => (value ? parseInt(value, 10) : undefined),
    stringify: (value) => (value !== undefined ? value.toString() : undefined),
  });

  readonly pageDaysBack = linkedQueryParam<number | null>('since_days', {
    parse: paramToNumber(),
  });

  readonly pageIncidentDateFrom = linkedQueryParam<Date | undefined>('incident_date_from', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : undefined),
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : undefined),
  });

  readonly pageIncidentDateTo = linkedQueryParam<Date | undefined>('incident_date_to', {
    parse: (value) => (value ? DateTime.fromFormat(value, 'yyyy-MM-dd').toJSDate() : undefined),
    stringify: (value) => (value ? DateTime.fromJSDate(value).toFormat('yyyy-MM-dd') : undefined),
  });

  public minIncidents = signal<number>(0);
  public activeTab = signal<GroceryCaseTab>('new');

  public loadingCases = signal<boolean>(false);
  public error = signal<string | undefined>(undefined);

  public defaultBranchIds = signal<number[]>([]);
  public selectedIncidentTypes = signal<string[]>([]);
  public statsPeriod = signal<number>(30);
  public selectedBranch = signal<Branch | undefined>(undefined);
  public selectedIncidentId = linkedQueryParam<string | undefined>('incident_id');

  public cases = signal<CaseWithIncidents[]>([]);

  public branchStatistics = computed(() => {
    const cases = this.cases();
    const branch = this.selectedBranch()!;
    const periodDays = this.statsPeriod();

    const now = new Date();

    const branchCases = cases.filter((c) => c.branch_id === branch.id);

    const periodStart = new Date(now.getTime() - periodDays * 24 * 60 * 60 * 1000);
    const prevPeriodStart = new Date(periodStart.getTime() - periodDays * 24 * 60 * 60 * 1000);

    const taggedIncidents = branchCases.flatMap((c) =>
      c.incidents.map((i) => ({ ...i, _caseId: c.id, _incidentDate: new Date(i.incident_date!) })),
    );

    const currentIncidents = taggedIncidents.filter((i) => i._incidentDate >= periodStart);
    const previousIncidents = taggedIncidents.filter((i) => i._incidentDate >= prevPeriodStart && i._incidentDate < periodStart);

    const currentCaseIds = new Set(currentIncidents.map((i) => i._caseId));
    const previousCaseIds = new Set(previousIncidents.map((i) => i._caseId));

    const totalCases = currentCaseIds.size;
    const prevTotalCases = previousCaseIds.size;
    const totalIncidents = currentIncidents.length;
    const prevTotalIncidents = previousIncidents.length;
    const totalLoss = currentIncidents.reduce((s, i) => s + (i.item_value ?? 0), 0);
    const prevLoss = previousIncidents.reduce((s, i) => s + (i.item_value ?? 0), 0);
    const totalLossRepresented = currentIncidents.reduce((s, i) => s + (i.loss_represented ?? 0), 0);
    const prevLossRepresented = previousIncidents.reduce((s, i) => s + (i.loss_represented ?? 0), 0);

    const pct = (curr: number, prev: number) => (prev === 0 ? (curr > 0 ? 100 : 0) : Math.round(((curr - prev) / prev) * 100));

    return {
      branch_id: branch.id,
      branch_name: branch.name,
      total_cases: totalCases,
      total_incidents: totalIncidents,
      total_loss: totalLoss,
      total_loss_represented: totalLossRepresented,
      cases_growth_pct: pct(totalCases, prevTotalCases),
      incidents_growth_pct: pct(totalIncidents, prevTotalIncidents),
      loss_growth_pct: pct(totalLoss, prevLoss),
      loss_represented_growth_pct: pct(totalLossRepresented, prevLossRepresented),
    };
  });

  public disableActions = computed(() => this.loadingCases());

  public selectedIncidentCaseTitle = computed(() => {
    const incident = this.selectedIncidentRow();
    if (!incident) return 'Incident Details';
    return `${incident.location_name} | ${incident.case_index}-${incident.instance_num}`;
  });

  public filteredTableEntries = computed(() => {
    const allCases = this.cases();
    const minIncidents = this.minIncidents();
    const displayArchived = this.activeTab() === 'archived';
    const typeFilters = this.selectedIncidentTypes();
    const dateFrom = this.pageIncidentDateFrom();
    const dateTo = this.pageIncidentDateTo();

    const entries: GroceryCaseTableEntry[] = allCases
      .map((c) => {
        let incidents: GroceryCaseIncidentEntry[] = c.incidents
          .map((i) => ({
            case_id: c.id,
            cashier_id: i.cashier_id ?? c.metadata.cashier_id,
            name: c.metadata.name,
            case_index: c.case_index,
            instance_num: i.instance_num,
            review_id: i.review_id!,
            review_outcome_id: i.review_outcome_id,
            review_summary: i.summary ?? '',
            terminal_id: i.terminal_id,
            incident_date: new Date(i.incident_date!),
            incident_types: i.incident_types ?? [],
            loss_amount: i.item_value ?? 0,
            loss_represented: i.loss_represented ?? 0,
            status: i.status,
            video_links: i.video_links ?? [],
            attachments: i.attachments ?? [],
            use_kiosk_photo: i.use_kiosk_photo,
            image_id: i.image_id,
            location_name: c.location_name,
            branch_name: c.branch_name,
          }))
          .filter((i) => (displayArchived ? i.status === CaseStatus.Archived : i.status !== CaseStatus.Archived));

        if (dateFrom) {
          incidents = incidents.filter((i) => i.incident_date >= dateFrom);
        }

        if (dateTo) {
          const endOfDay = new Date(dateTo);
          endOfDay.setHours(23, 59, 59, 999);
          incidents = incidents.filter((i) => i.incident_date <= endOfDay);
        }

        if (typeFilters.length > 0) {
          incidents = incidents.filter((i) => i.incident_types.some((type) => typeFilters.includes(this.getIncidentTypeLabel(type))));
        }

        return {
          case_id: c.id,
          cashier_id: c.metadata.cashier_id,
          name: c.metadata.name ?? '',
          location_name: c.location_name,
          branch_name: c.branch_name,
          branch_id: c.branch_id,
          location_id: c.location_id,
          incident_count: incidents.length,
          total_loss_amount: sumBy(incidents, 'loss_amount'),
          total_loss_represented: sumBy(incidents, 'loss_represented'),
          status: c.status,
          incidents,
        };
      })
      .filter((entry) => entry.incidents.length > 0 && entry.incident_count >= minIncidents);

    return entries;
  });

  public readonly caseStatusOptions: string[] = [
    CaseStatus.New,
    CaseStatus.Viewed,
    CaseStatus.ViewedByClient,
    CaseStatus.Resolved,
    CaseStatus.Reopened,
    CaseStatus.Archived,
  ];

  // also lists the types found in the loaded cases, so a type without a label can still be filtered.
  // filters by label, since some types are saved under more than one code
  public readonly incidentTypeOptions = computed<string[]>(() => {
    const loadedTypes = this.cases().flatMap((c) => c.incidents.flatMap((i) => i.incident_types ?? []));
    const codes = [...Object.keys(INCIDENT_TYPE_LABELS), ...loadedTypes];
    return sortBy(uniq(codes.map((code) => this.getIncidentTypeLabel(code))));
  });

  public readonly columnDefs: ColDef<GroceryCaseTableEntry>[] = [
    {
      headerName: 'Case ID',
      sortable: true,
      flex: 2,
      filter: 'agTextColumnFilter',
      suppressHeaderMenuButton: false,
      cellRenderer: 'agGroupCellRenderer',
      valueGetter: (params) => {
        const entry = params.data;
        if (!entry) return '';
        return `${entry.name || entry.cashier_id || entry.case_id || 'N/A'}`;
      },
      tooltipValueGetter: (params) => {
        const entry = params.data;
        if (!entry) return '';
        return `Case ID: ${entry.case_id} | Name: ${entry.name ?? 'N/A'} | Cashier ID: ${entry.cashier_id ?? 'N/A'}`;
      },
      contextMenuItems: (params) => {
        return [
          'copy',
          {
            name: 'Rename Case ID',
            menuItem: AGGridMenuItem,
            menuItemParams: {
              itemClicked: async () => {
                const result = await Swal.fire({
                  title: 'Rename Case ID',
                  input: 'text',
                  inputLabel: 'New Case ID',
                  inputPlaceholder: 'Enter new case ID',
                  icon: 'question',
                  showCancelButton: true,
                  confirmButtonText: 'Rename',
                  cancelButtonText: 'Cancel',
                });

                if (result.isConfirmed) {
                  const caseId = params.node?.data?.case_id;
                  if (!caseId) {
                    this.notifyService.notify('Unable to rename case: Case ID not found');
                    return;
                  }

                  const newName = result.value.trim();

                  this.caseTrackingService.updateCaseName(caseId, newName).subscribe({
                    next: () => {
                      if (params.node?.data) {
                        params.node.data.name = newName;
                        params.api.refreshCells({ rowNodes: [params.node], force: true });
                      }

                      this.notifyService.notify('Case ID renamed successfully');
                    },
                  });
                }
              },
            },
          },
        ];
      },
    },
    {
      headerName: 'Store',
      field: 'location_name',
      sortable: true,
      flex: 2,
      filter: 'agTextColumnFilter',
      suppressHeaderMenuButton: false,
    },
    {
      headerName: '# Incidents',
      field: 'incident_count',
      sortable: true,
      flex: 1,
      filter: 'agNumberColumnFilter',
      suppressHeaderMenuButton: false,
    },
    {
      headerName: 'Loss Detected',
      field: 'total_loss_amount',
      sortable: true,
      flex: 1,
      filter: 'agNumberColumnFilter',
      suppressHeaderMenuButton: false,
      valueFormatter: (params) => formatCurrency(params.value ?? 0, 'en-US', '$'),
    },
    {
      headerName: 'Loss Represented',
      field: 'total_loss_represented',
      sortable: true,
      flex: 1,
      filter: 'agNumberColumnFilter',
      suppressHeaderMenuButton: false,
      valueFormatter: (params) => formatCurrency(params.value ?? 0, 'en-US', '$'),
    },
    {
      headerName: 'Status',
      field: 'status',
      sortable: true,
      flex: 1,
      filter: 'agTextColumnFilter',
      suppressHeaderMenuButton: false,
      cellRenderer: GroceryCtpStatusCellComponent,
    },
  ];

  private readonly detailColumnDefs: ColDef<GroceryCaseIncidentEntry>[] = [
    {
      headerName: 'Incident ID',
      sortable: false,
      valueGetter: (params) => {
        const data = params.data;
        if (!data) return '';
        return `${data.case_index}-${data.instance_num}`;
      },
    },
    {
      headerName: 'Register #',
      field: 'terminal_id',
      sortable: false,
      valueFormatter: (params: ValueFormatterParams) => params.value ?? 'N/A',
    },
    {
      headerName: 'Date',
      field: 'incident_date',
      sortable: false,
      valueFormatter: (params: ValueFormatterParams) => {
        if (!params.value) return '';
        return formatDate(params.value, 'MM/dd/yyyy', 'en-US');
      },
    },
    {
      headerName: 'Incident Type',
      field: 'incident_types',
      sortable: false,
      valueFormatter: (params: ValueFormatterParams) => this.getIncidentTypesLabel(params.value),
      // the grid only shows it when the types do not fit in the cell
      tooltipValueGetter: (params) => this.getIncidentTypesLabel(params.data?.incident_types) || undefined,
    },
    {
      headerName: 'Items',
      sortable: false,
      maxWidth: 90,
      valueGetter: (params) => {
        const items = params.data?.review_summary ?? '';
        if (!items.trim()) return 0;
        return items.split('\n').filter((line: string) => line.trim().length > 0).length;
      },
    },
    {
      headerName: 'Loss Detected',
      field: 'loss_amount',
      sortable: false,
      valueFormatter: (params) => formatCurrency(params.value ?? 0, 'en-US', '$'),
    },
    {
      headerName: 'Loss Represented',
      field: 'loss_represented',
      sortable: false,
      valueFormatter: (params) => formatCurrency(params.value ?? 0, 'en-US', '$'),
    },
    {
      headerName: 'Incident Status',
      field: 'status',
      sortable: false,
      minWidth: 170,
      cellRenderer: GroceryCtpStatusCellComponent,
    },
    {
      headerName: '',
      sortable: false,
      maxWidth: 160,
      suppressKeyboardEvent: () => true,
      cellClass: ['inline-flex', 'items-center', 'justify-end', 'gap-2'],
      cellRenderer: GroceryCtpViewActionCellComponent,
      cellRendererParams: {
        viewCallback: (params: ICellRendererParams<GroceryCaseIncidentEntry>) => {
          this.openIncidentDetail(params.data!);
          // Refresh the detail row to reflect status change (e.g. New → Viewed)
          params.api.refreshCells({ rowNodes: [params.node], force: true });
          // Refresh the master row using the stored master grid API
          if (this.masterGridApi) {
            const masterRowNode = this.masterGridApi.getRowNode(params.data?.case_id ?? '');
            if (masterRowNode) {
              this.masterGridApi.refreshCells({ rowNodes: [masterRowNode], force: true });
            }
          }
        },
      },
    },
  ];

  public readonly tableDef: GridOptions<GroceryCaseTableEntry> = {
    ...defaultTableDefOptions(),
    rowHeight: 56,
    masterDetail: true,
    detailRowAutoHeight: true,
    isRowMaster: (dataItem) => (dataItem?.incident_count ?? 0) > 0,
    onRowSelected: (event: RowSelectedEvent<GroceryCaseTableEntry>) => {
      const item = event.data;
      if (!item) return;

      this.selectedCases.update((current) => {
        const alreadySelected = current.some((c) => c.case_id === item.case_id);
        if (alreadySelected) {
          return current.filter((c) => c.case_id !== item.case_id);
        }
        return [...current, item];
      });

      this.refreshSelectionConstraints();
    },
    detailCellRendererParams: {
      refreshStrategy: 'everything',
      detailGridOptions: {
        ...defaultTableDefOptions(),
        columnDefs: this.detailColumnDefs,
        domLayout: 'autoHeight',
        defaultColDef: {
          resizable: false,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          sortable: false,
          cellClass: ['inline-flex', 'items-center'],
        },
        rowSelection: this.getDetailRowSelection(),
        onFirstDataRendered: () => {
          this.refreshSelectionConstraints();
        },
        onRowSelected: (event: RowSelectedEvent<GroceryCaseIncidentEntry>) => {
          const item = event.data;
          if (!item) return;

          this.selectedIncidents.update((current) => {
            const alreadySelected = current.some((c) => c.review_outcome_id === item.review_outcome_id);
            if (alreadySelected) {
              return current.filter((c) => c.review_outcome_id !== item.review_outcome_id);
            }
            return [...current, item];
          });

          this.refreshSelectionConstraints();
        },
      },
      getDetailRowData: (params: GetDetailRowDataParams<GroceryCaseTableEntry, GroceryCaseIncidentEntry>) => {
        params.successCallback(params.data.incidents ?? []);
      },
    },
    rowSelection: this.getMasterRowSelection(),
    selectionColumnDef: {
      maxWidth: 55,
      suppressHeaderMenuButton: true,
    },
    pagination: true,
    paginationPageSize: 10,
    paginationPageSizeSelector: [10, 20, 50],
  };

  public getRowId = (params: { data: GroceryCaseTableEntry }) => params.data.case_id;

  onGridReady(event: GridReadyEvent<GroceryCaseTableEntry>) {
    this.masterGridApi = event.api;
    this.refreshSelectionConstraints();
  }

  ngAfterViewInit() {
    this.loadFromURLParams();
  }

  public getIncidentTypeLabel(code: string | undefined | null): string {
    if (!code) return '';
    return INCIDENT_TYPE_LABELS[code] ?? code;
  }

  public getIncidentTypesLabel(codes: string[] | undefined | null): string {
    return (codes ?? []).map((code) => this.getIncidentTypeLabel(code)).join(', ');
  }

  private loadFromURLParams() {
    const branchId = this.pageBranchId();
    const daysBack = this.pageDaysBack();
    const incidentFrom = this.pageIncidentDateFrom();
    const incidentTo = this.pageIncidentDateTo();

    this.defaultBranchIds.set(branchId ? [branchId] : []);

    if (incidentFrom && incidentTo) {
      this.updatePeriodBasedOnCustomDates();
    }

    if (branchId || daysBack || incidentFrom || incidentTo) this.refresh();
  }

  onBranchSelection() {
    if (!this.pageIncidentDateTo() || !this.pageIncidentDateTo()) {
      this.pageIncidentDateFrom.set(DateTime.now().minus({ days: this.statsPeriod() }).toJSDate());
      this.pageIncidentDateTo.set(DateTime.now().toJSDate());
    }

    this.onSelection();
  }

  onSelection() {
    this.error.set(undefined);
    this.refresh();
  }

  setActiveTab(tab: GroceryCaseTab) {
    this.activeTab.set(tab);
    this.selectedCases.set([]);
    this.selectedIncidents.set([]);
    this.refreshSelectionConstraints();
  }

  private updatePeriodBasedOnCustomDates() {
    const from = this.pageIncidentDateFrom();
    const to = this.pageIncidentDateTo();
    const daysBetween = from && to ? Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) : null;
    if (daysBetween !== null) {
      this.statsPeriod.set(daysBetween);
    }
  }

  updatePeriodAndRefresh() {
    this.updatePeriodBasedOnCustomDates();
    this.refresh();
  }

  updatePeriod(period: number) {
    this.pageIncidentDateFrom.set(DateTime.now().minus({ days: period }).toJSDate());
    this.pageIncidentDateTo.set(DateTime.now().toJSDate());

    this.refresh();
  }

  refresh() {
    this.updateDeepLink();

    const branch = this.selectedBranch();

    if (!branch) {
      return;
    }

    this.loadingCases.set(true);
    const request: GetCasesByLocationsRequest = {
      location_ids: [],
      branch_ids: [branch.id],
      location_group_ids: [],
      incident_date_from: this.pageIncidentDateFrom(),
      incident_date_to: this.pageIncidentDateTo(),
      mode: 'default',
    };

    this.caseTrackingService
      .getCasesByLocations(request)
      .pipe(
        finalize(() => this.loadingCases.set(false)),
        map((response: Pagination<CaseWithIncidents>) => {
          const updatedItems = (response.items ?? []).map((caseItem) => ({
            ...caseItem,
            incidents: (caseItem.incidents ?? []).map((incident) => ({
              ...incident,
              loss_represented: (incident.item_value ?? 0) * 8,
            })),
          }));

          return { ...response, items: updatedItems };
        }),
      )
      .subscribe({
        next: (response: Pagination<CaseWithIncidents>) => {
          this.cases.set(response.items ?? []);

          this.attemptAutoLoadCaseFromURL();
        },
        error: (err) => this.error.set(tryGetErrorMessage(err)),
      });
  }

  attemptAutoLoadCaseFromURL() {
    const incidentId = this.selectedIncidentId();
    if (!incidentId) return;

    const allCases = this.filteredTableEntries();
    for (const caseItem of allCases) {
      const incident = caseItem.incidents.find((i) => i.review_id === incidentId);
      if (incident) {
        this.openIncidentDetail(incident);
        break;
      }
    }
  }

  updateDeepLink() {
    const branch = this.selectedBranch();
    this.pageBranchId.set(branch ? branch.id : undefined);
  }

  openIncidentDetail(row: GroceryCaseIncidentEntry) {
    if (!row) return;
    if (!row.review_id) return;

    this.selectedIncidentId.set(row.review_id);

    this.selectedIncidentRow.set(row);
    this.isLoadingInvestigation.set(true);
    this.selectedInvestigation.set(null);
    this.currentEvidenceIndex.set(0);
    this.detailModal()?.show();

    const caseItem = this.filteredTableEntries().find((c) => c.case_id === row.case_id);

    if (caseItem?.status === CaseStatus.New) {
      caseItem.status = CaseStatus.Viewed;
    }

    if (row.status === CaseStatus.New) {
      row.status = CaseStatus.Viewed;
    }

    this.theftReportService
      .getSharedInvestigation(row.review_id)
      .pipe(finalize(() => this.isLoadingInvestigation.set(false)))
      .subscribe({
        next: (investigation) => this.selectedInvestigation.set(investigation),
        error: () => this.selectedInvestigation.set(null),
      });
  }

  closeDetailModal() {
    this.detailModal()?.hide();
    this.selectedInvestigation.set(null);
    this.selectedIncidentRow.set(undefined);
    this.currentEvidenceIndex.set(0);
    this.selectedIncidentId.set(undefined);
  }

  previousEvidence() {
    this.currentEvidenceIndex.update((i) => Math.max(0, i - 1));
  }

  nextEvidence() {
    this.currentEvidenceIndex.update((i) => Math.min(this.evidenceItems().length - 1, i + 1));
  }

  archiveSelectedCases() {
    const casesToArchive = this.selectedCases().filter((c) => c.status !== CaseStatus.Archived);
    if (casesToArchive.length === 0) return;

    this.loadingCases.set(true);

    const calls = casesToArchive.map((c) => this.caseTrackingService.archiveCase(c.case_id));
    forkJoin(calls)
      .pipe(finalize(() => this.loadingCases.set(false)))
      .subscribe({
        next: () => {
          this.refresh();
          this.selectedCases.set([]);
          this.refreshSelectionConstraints();
        },
      });
  }

  restoreSelectedCases() {
    const casesToRestore = this.selectedCases().filter((c) => c.status === CaseStatus.Archived);
    if (casesToRestore.length === 0) return;

    this.loadingCases.set(true);

    const calls = casesToRestore.map((c) => this.caseTrackingService.restoreCase(c.case_id));
    forkJoin(calls)
      .pipe(finalize(() => this.loadingCases.set(false)))
      .subscribe({
        next: () => {
          this.refresh();
          this.selectedCases.set([]);
          this.refreshSelectionConstraints();
        },
      });
  }

  archiveSelectedIncidents() {
    const incidentsToArchive = this.selectedIncidents().filter((i) => i.status !== CaseStatus.Archived);
    if (incidentsToArchive.length === 0) return;

    forkJoin([...incidentsToArchive.map((i) => this.caseTrackingService.archiveReviewOutcome(i.case_id, i.review_outcome_id))]).subscribe({
      next: () => {
        this.refresh();
        this.selectedIncidents.set([]);
        this.refreshSelectionConstraints();
      },
    });
  }

  restoreSelectedIncidents() {
    const incidentsToRestore = this.selectedIncidents().filter((i) => i.status === CaseStatus.Archived);
    if (incidentsToRestore.length === 0) return;

    forkJoin([...incidentsToRestore.map((i) => this.caseTrackingService.restoreReviewOutcome(i.case_id, i.review_outcome_id))]).subscribe({
      next: () => {
        this.refresh();
        this.selectedIncidents.set([]);
        this.refreshSelectionConstraints();
      },
    });
  }

  currentPageURL(): string {
    return window.location.toString();
  }
}
